import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Zap, BarChart3, Shield, RefreshCw, Layers, Globe, CheckCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import DemoForm from "@/components/forms/DemoForm";

const features = [
  {
    icon: Zap,
    title: "Pricing Dinámico Automatizado",
    description: "Ajusta tarifas en tiempo real basándote en demanda, competencia, eventos y más de 50 variables de mercado.",
  },
  {
    icon: BarChart3,
    title: "Forecasting con IA",
    description: "Predice demanda con precisión superior al 95% utilizando algoritmos de machine learning entrenados con datos del sector.",
  },
  {
    icon: Shield,
    title: "Control Total",
    description: "Define pisos y techos de tarifas, reglas de negocio y aprobaciones. Tú mantienes el control, el sistema optimiza.",
  },
  {
    icon: RefreshCw,
    title: "Actualizaciones en Tiempo Real",
    description: "Sincronización instantánea con tu PMS y channel manager para que nunca pierdas una oportunidad.",
  },
  {
    icon: Layers,
    title: "Multi-propiedad",
    description: "Gestiona múltiples hoteles desde un solo dashboard con benchmarking interno y estrategias personalizadas.",
  },
  {
    icon: Globe,
    title: "Análisis de Competencia",
    description: "Monitorea precios de competidores en tiempo real y ajusta tu estrategia para mantener competitividad.",
  },
];

const integrations = [
  "Opera PMS",
  "Cloudbeds",
  "PriceTravel",
  "Easy Rez",
  "Booking.com",
  "Expedia",
  "SiteMinder",
  "Google Hotels",
];

export default function SoftwareRMSPage() {
  useEffect(() => {
    document.title = "Software RMS para Hoteles | Sistema de Revenue Management - Zimoo";
    
    // Schema markup for SoftwareApplication
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "SoftwareApplication",
      "name": "Zimoo Revenue Management System",
      "applicationCategory": "BusinessApplication",
      "operatingSystem": "Web",
      "offers": {
        "@type": "Offer",
        "price": "0",
        "priceCurrency": "USD",
        "description": "Demo gratuita disponible"
      },
      "description": "Sistema de Revenue Management para hoteles con pricing dinámico automatizado y forecasting con inteligencia artificial.",
      "aggregateRating": {
        "@type": "AggregateRating",
        "ratingValue": "4.8",
        "ratingCount": "150"
      }
    });
    document.head.appendChild(script);
    
    return () => {
      document.head.removeChild(script);
    };
  }, []);

  return (
    <div className="pt-20">
      {/* Hero */}
      <section className="section-padding bg-gradient-to-b from-[#FAFAFA] to-white grain-overlay">
        <div className="max-w-7xl mx-auto container-padding relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="label-text mb-4 block">SOFTWARE RMS</span>
              <h1 className="heading-1 mb-6">
                Sistema de Revenue Management{" "}
                <span className="text-gradient">Inteligente</span>
              </h1>
              <p className="body-text text-lg mb-8">
                La plataforma más completa para optimizar los ingresos de tu hotel. Pricing dinámico, forecasting con IA y automatización total en un solo lugar.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <a href="#demo-rms">
                  <Button className="btn-primary">
                    Contáctanos
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                </a>
              </div>
            </div>
            <div className="relative">
              <img
                src="https://images.pexels.com/photos/106344/pexels-photo-106344.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                alt="Dashboard de Analytics"
                className="rounded-2xl shadow-2xl"
              />
              <div className="absolute -bottom-4 -right-4 bg-white rounded-xl shadow-lg p-4 border border-black/5">
                <p className="text-sm text-[#404040]">Precisión de forecasting</p>
                <p className="text-3xl font-bold text-[#1ABCB4]">95%+</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* AEO Block */}
      <section className="py-12 bg-[#0A0A0A]">
        <div className="max-w-4xl mx-auto container-padding text-center">
          <h2 className="text-xl font-semibold text-white mb-3">¿Cómo funciona un RMS?</h2>
          <p className="text-gray-300 leading-relaxed">
            Un Sistema de Revenue Management (RMS) analiza datos históricos, demanda del mercado, precios de competencia y eventos locales para calcular automáticamente el precio óptimo de cada habitación. Utiliza inteligencia artificial para predecir demanda y ajustar tarifas en tiempo real, maximizando los ingresos del hotel.
          </p>
        </div>
      </section>

      {/* Features Grid */}
      <section className="section-padding bg-white">
        <div className="max-w-7xl mx-auto container-padding">
          <div className="text-center mb-16">
            <span className="label-text mb-4 block">FUNCIONALIDADES</span>
            <h2 className="heading-2 mb-4">
              Todo lo que necesitas para maximizar ingresos
            </h2>
          </div>

          <div className="bento-grid">
            {features.map((feature, index) => (
              <div key={index} className="card-hover p-8">
                <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-[#1ABCB4] to-[#5200FF] flex items-center justify-center mb-6">
                  <feature.icon className="h-7 w-7 text-white" />
                </div>
                <h3 className="text-xl font-semibold text-[#0A0A0A] mb-3">
                  {feature.title}
                </h3>
                <p className="text-[#404040]">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How it Works */}
      <section className="section-padding bg-[#FAFAFA]">
        <div className="max-w-7xl mx-auto container-padding">
          <div className="text-center mb-16">
            <span className="label-text mb-4 block">PROCESO</span>
            <h2 className="heading-2 mb-4">
              Implementación rápida y sin interrupciones
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {[
              { step: "01", title: "Conexión", desc: "Integramos con tu PMS en menos de 24 horas" },
              { step: "02", title: "Análisis", desc: "Procesamos tu data histórica y del mercado" },
              { step: "03", title: "Configuración", desc: "Definimos reglas y estrategia inicial" },
              { step: "04", title: "Optimización", desc: "El sistema comienza a optimizar automáticamente" },
            ].map((item, index) => (
              <div key={index} className="text-center">
                <div className="w-16 h-16 rounded-full bg-[#1ABCB4] text-white text-2xl font-bold flex items-center justify-center mx-auto mb-4">
                  {item.step}
                </div>
                <h3 className="text-lg font-semibold text-[#0A0A0A] mb-2">{item.title}</h3>
                <p className="text-[#404040] text-sm">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Integrations */}
      <section className="section-padding bg-white">
        <div className="max-w-7xl mx-auto container-padding">
          <div className="text-center mb-12">
            <span className="label-text mb-4 block">INTEGRACIONES</span>
            <h2 className="heading-2 mb-4">
              Se conecta con tus herramientas
            </h2>
            <p className="body-text max-w-2xl mx-auto">
              Zimoo se integra con los principales PMS, channel managers y OTAs del mercado.
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {integrations.map((integration, index) => (
              <div
                key={index}
                className="bg-[#FAFAFA] rounded-xl p-6 text-center border border-black/5 hover:border-[#1ABCB4]/30 transition-colors"
              >
                <p className="font-medium text-[#0A0A0A]">{integration}</p>
              </div>
            ))}
          </div>
          <p className="text-center text-[#404040] mt-6">
            Y más de 50 integraciones adicionales...
          </p>
        </div>
      </section>

      {/* Benefits */}
      <section className="section-padding bg-[#FAFAFA]">
        <div className="max-w-7xl mx-auto container-padding">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="label-text mb-4 block">BENEFICIOS</span>
              <h2 className="heading-2 mb-6">
                ¿Por qué elegir Zimoo?
              </h2>
              <ul className="space-y-4">
                {[
                  "Incremento promedio del 20% en RevPAR",
                  "ROI positivo desde el primer mes",
                  "Ahorro de +15 horas semanales en gestión manual",
                  "Soporte técnico 24/7 en español",
                  "Sin contratos de permanencia",
                  "Capacitación incluida para tu equipo",
                  "Actualizaciones constantes sin costo adicional",
                  "Seguridad de datos de nivel empresarial"
                ].map((benefit, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <CheckCircle className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
                    <span className="text-[#404040]">{benefit}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <img
                src="https://images.unsplash.com/photo-1759038085950-1234ca8f5fed?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzF8MHwxfHNlYXJjaHwxfHxob3RlbCUyMHJlY2VwdGlvbiUyMGRlc2slMjBtb2Rlcm58ZW58MHx8fHwxNzc0Mjk0Nzk0fDA&ixlib=rb-4.1.0&q=85&w=800"
                alt="Recepción de hotel moderno"
                className="rounded-2xl shadow-xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section id="demo-rms" className="section-padding bg-white">
        <div className="max-w-7xl mx-auto container-padding">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="heading-2 mb-6">
                Contáctanos
              </h2>
              <p className="body-text mb-6">
                Te mostraremos cómo Zimoo puede transformar la gestión de ingresos de tu hotel. Sin compromiso, sin costos ocultos.
              </p>
              <div className="flex items-center gap-4">
                <div className="flex -space-x-2">
                  {[1,2,3].map(i => (
                    <div key={i} className="w-10 h-10 rounded-full bg-gradient-to-br from-[#1ABCB4] to-[#5200FF] border-2 border-white" />
                  ))}
                </div>
                <p className="text-sm text-[#404040]">
                  <span className="font-semibold text-[#0A0A0A]">+hoteles</span> ya confían en Zimoo
                </p>
              </div>
            </div>
            <DemoForm sourcePage="software-rms" />
          </div>
        </div>
      </section>
    </div>
  );
}
